/**
 * Typed Shopify webhook event catalog.
 *
 * Shopify dispatches each topic with a different body shape. The
 * topic identifier lives in the `X-Shopify-Topic` header (e.g.
 * `orders/paid`). This catalog covers 13 identity-bearing topics across
 * customers, orders, checkouts, and fulfillments. Excluded: `carts/update`
 * (Shopify sends no recipient identity) and refunds — Shopify has no
 * `orders/refunded` topic, and its `refunds/create` ("Refund create") payload
 * carries no customer either. Both retired keys are hidden by
 * `convex/triggerEvents/retired_provider_events.ts`.
 *
 * Shopify sends a topic only to a webhook created for it (one webhook per
 * topic in Shopify Admin), so each entry names its Shopify Admin event in
 * `sourceEventLabel` for the setup checklist.
 *
 * Schema notes (verified May 2026, sourced from the official Shopify
 * developer changelog):
 *  - March 2026: `id` → `token` on `checkouts/*` payloads
 *    (https://shopify.dev/changelog/removed-checkout-id-from-checkouts-and-orders-webhooks).
 *    Brew uses `token` as the canonical checkout identifier and falls
 *    back to legacy `id` only for pre-migration deliveries.
 *  - January 2025: customer-object fields `tags`, `email_marketing_consent`,
 *    `sms_marketing_consent`, `last_order_id`, `last_order_name`,
 *    `total_spent`, and `orders_count` were removed from `customers/*`
 *    payloads
 *    (https://shopify.dev/changelog/new-customer-s-webhook-and-changes-to-existing-customer-s-webhooks-payload).
 *    Brew's `tags` extraction is best-effort and only fires for
 *    legacy / non-2025-01 shop API versions.
 *  - January 2025: order payloads no longer embed the full customer
 *    object inline — email is at `order.customer.email` for
 *    registered customers or top-level `order.email` for guest
 *    checkouts. Brew handles both via `resolveCustomerInfo`.
 */

import type {
  InboundEventCategory,
  InboundEventMetadata,
} from '@/lib/integrations/inbound/types'
import type {
  IntegrationTriggerField,
  IntegrationTriggerPayloadSchema,
} from '@/lib/triggers/types'

type TriggerField = IntegrationTriggerField
type TriggerPayloadSchema = IntegrationTriggerPayloadSchema
export type SupportedShopifyEventType =
  | 'customers/create'
  | 'customers/update'
  | 'customers/delete'
  | 'customers/enable'
  | 'customers/disable'
  | 'orders/create'
  | 'orders/paid'
  | 'orders/cancelled'
  | 'orders/fulfilled'
  | 'checkouts/create'
  | 'checkouts/update'
  | 'fulfillments/create'
  | 'fulfillments/update'

export const SUPPORTED_SHOPIFY_EVENTS: ReadonlyArray<SupportedShopifyEventType> =
  [
    'customers/create',
    'customers/update',
    'customers/delete',
    'customers/enable',
    'customers/disable',
    'orders/create',
    'orders/paid',
    'orders/cancelled',
    'orders/fulfilled',
    'checkouts/create',
    'checkouts/update',
    'fulfillments/create',
    'fulfillments/update',
  ] as const

const SUPPORTED_SHOPIFY_EVENT_SET = new Set<string>(SUPPORTED_SHOPIFY_EVENTS)

/**
 * Slim user-facing common preamble. Shopify webhook metadata (event id,
 * topic, object id/type) is intentionally absent — the extractor still
 * emits those keys for the contact mapper to read, but they have no
 * place on a marketer's merge-tag picker. `shopDomain` IS declared:
 * multi-store setups filter/split on which shop fired the event.
 */
const COMMON_FIELDS: Array<TriggerField> = [
  {
    key: 'email',
    type: 'string',
    required: true,
    description:
      'Recipient email derived from the Shopify event. Synthetic placeholder for checkout events that ship without one.',
    exampleValue: 'shopper@example.com',
  },
  {
    key: 'firstName',
    type: 'string',
    required: false,
    description: 'Customer first name when present.',
    exampleValue: 'Alex',
  },
  {
    key: 'lastName',
    type: 'string',
    required: false,
    description: 'Customer last name when present.',
    exampleValue: 'Morgan',
  },
  {
    key: 'currency',
    type: 'string',
    required: false,
    description: 'Order / checkout currency when present.',
    exampleValue: 'USD',
  },
  {
    key: 'shopDomain',
    type: 'string',
    required: false,
    description:
      'The `*.myshopify.com` domain of the shop that fired the event. Filter/split on it in multi-store setups.',
    exampleValue: 'acme.myshopify.com',
  },
  {
    key: 'createdAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) derived from `created_at` when available.',
    exampleValue: 1_710_000_000,
  },
]

const CUSTOMER_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'tags',
    type: 'string',
    required: false,
    description:
      'Comma-separated Shopify customer tags when present. NOTE: removed from `customers/*` webhooks in 2025-01 API — use the dedicated `customers/tags_added` / `customers/tags_removed` webhooks for tag changes on the 2025-01 API or later.',
    exampleValue: 'vip,wholesale',
  },
]

const ORDER_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'orderNumber',
    type: 'string',
    required: false,
    description: 'Human-friendly order number (e.g. `#1042`) when available.',
    exampleValue: '#1042',
  },
  {
    key: 'totalPrice',
    type: 'string',
    required: false,
    description: 'Order total in the order currency.',
    exampleValue: '49.95',
  },
  {
    key: 'financialStatus',
    type: 'string',
    required: false,
    description: 'Order financial status (`paid`, `pending`, etc).',
    exampleValue: 'paid',
  },
  {
    key: 'fulfillmentStatus',
    type: 'string',
    required: false,
    description: 'Order fulfillment status when present.',
    exampleValue: 'fulfilled',
  },
]

const CHECKOUT_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'totalPrice',
    type: 'string',
    required: false,
    description: 'Checkout total when present.',
    exampleValue: '49.95',
  },
  {
    key: 'abandonedCheckoutUrl',
    type: 'string',
    required: false,
    description:
      'URL the customer can use to resume the abandoned checkout when present.',
    exampleValue: 'https://acme.myshopify.com/checkouts/c/abandoned',
  },
]

const FULFILLMENT_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'trackingNumber',
    type: 'string',
    required: false,
    description: 'Carrier tracking number when present.',
    exampleValue: '1Z999AA10123456789',
  },
  {
    key: 'trackingUrl',
    type: 'string',
    required: false,
    description: 'Carrier tracking URL when present.',
    exampleValue: 'https://www.ups.com/track?tracknum=1Z999AA10123456789',
  },
  {
    key: 'fulfillmentStatus',
    type: 'string',
    required: false,
    description: 'Fulfillment status (`pending`, `success`, `failure`, etc).',
    exampleValue: 'success',
  },
]

function buildPayloadSchema(
  extras: ReadonlyArray<TriggerField>
): TriggerPayloadSchema {
  return {
    type: 'object',
    fields: [...COMMON_FIELDS, ...extras],
  }
}

function buildSamplePayload(
  _eventType: SupportedShopifyEventType,
  payload: Record<string, string | number | boolean | null>
): Record<string, string | number | boolean | null> {
  return {
    email: 'shopper@example.com',
    firstName: 'Alex',
    lastName: 'Morgan',
    shopDomain: 'acme.myshopify.com',
    ...payload,
  }
}

export const SHOPIFY_EVENT_CATEGORIES: ReadonlyArray<InboundEventCategory> = [
  { id: 'customer', label: 'Customer' },
  { id: 'order', label: 'Order' },
  { id: 'checkout', label: 'Checkout' },
  { id: 'fulfillment', label: 'Fulfillment' },
]

export const SHOPIFY_EVENT_METADATA: Array<InboundEventMetadata> = [
  {
    eventType: 'customers/create',
    title: 'Shopify - Customer Created',
    sourceEventLabel: 'Customer creation',
    description:
      'Fires when a new customer is created in Shopify. Use to send a welcome email or sync the contact into Brew.',
    category: 'customer',
    payloadSchema: buildPayloadSchema(CUSTOMER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('customers/create', {
      firstName: 'Alex',
      lastName: 'Morgan',
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'Welcome email',
        description: 'Greet new shoppers with a first-purchase nudge.',
      },
    ],
  },
  {
    eventType: 'customers/update',
    title: 'Shopify - Customer Updated',
    sourceEventLabel: 'Customer update',
    description: 'Fires when a Shopify customer record changes.',
    category: 'customer',
    payloadSchema: buildPayloadSchema(CUSTOMER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('customers/update', {
      firstName: 'Alex',
      lastName: 'Morgan',
    }),
    useCases: [
      {
        title: 'Contact sync',
        description: 'Mirror customer changes into Brew contacts.',
      },
    ],
  },
  {
    eventType: 'customers/delete',
    title: 'Shopify - Customer Deleted',
    sourceEventLabel: 'Customer deletion',
    description: 'Fires when a Shopify customer is deleted.',
    category: 'customer',
    payloadSchema: buildPayloadSchema(CUSTOMER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('customers/delete', {}),
    useCases: [
      {
        title: 'Drop from audiences',
        description: 'Remove deleted customers from Brew audiences.',
      },
    ],
  },
  {
    eventType: 'customers/enable',
    title: 'Shopify - Customer Enabled',
    sourceEventLabel: 'Customer enable',
    description: 'Fires when a Shopify customer account is re-enabled.',
    category: 'customer',
    payloadSchema: buildPayloadSchema(CUSTOMER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('customers/enable', {}),
    useCases: [
      {
        title: 'Reactivation email',
        description: 'Welcome a re-enabled customer back.',
      },
    ],
  },
  {
    eventType: 'customers/disable',
    title: 'Shopify - Customer Disabled',
    sourceEventLabel: 'Customer disable',
    description: 'Fires when a Shopify customer account is disabled.',
    category: 'customer',
    payloadSchema: buildPayloadSchema(CUSTOMER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('customers/disable', {}),
    useCases: [
      {
        title: 'Suppress sends',
        description: 'Pause marketing sends to a disabled account.',
      },
    ],
  },
  {
    eventType: 'orders/create',
    title: 'Shopify - Order Created',
    sourceEventLabel: 'Order creation',
    description: 'Fires when a new order is placed.',
    category: 'order',
    payloadSchema: buildPayloadSchema(ORDER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('orders/create', {
      orderNumber: '#1042',
      totalPrice: '49.95',
      currency: 'USD',
      financialStatus: 'pending',
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'Order confirmation',
        description: 'Confirm the order with totals + line items.',
      },
      {
        title: 'Fraud-check trigger',
        description: 'Hand off to your fraud-screening pipeline.',
      },
    ],
  },
  {
    eventType: 'orders/paid',
    title: 'Shopify - Order Paid',
    sourceEventLabel: 'Order payment',
    description: 'Fires when payment is captured on an order.',
    category: 'order',
    payloadSchema: buildPayloadSchema(ORDER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('orders/paid', {
      orderNumber: '#1042',
      totalPrice: '49.95',
      currency: 'USD',
      financialStatus: 'paid',
    }),
    useCases: [
      {
        title: 'Receipt',
        description: 'Send the receipt + thank-you message.',
      },
      {
        title: 'Post-purchase upsell',
        description: 'Trigger a follow-up campaign with related products.',
      },
      {
        title: 'VIP triage',
        description: 'Route high-value orders to a dedicated lifecycle flow.',
      },
    ],
  },
  {
    eventType: 'orders/cancelled',
    title: 'Shopify - Order Cancelled',
    sourceEventLabel: 'Order cancellation',
    description: 'Fires when an order is cancelled.',
    category: 'order',
    payloadSchema: buildPayloadSchema(ORDER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('orders/cancelled', {
      orderNumber: '#1042',
      financialStatus: 'voided',
    }),
    useCases: [
      {
        title: 'Cancellation confirmation',
        description: 'Confirm the cancellation with the shopper.',
      },
      {
        title: 'Win-back',
        description: 'Suggest related products or a discount.',
      },
    ],
  },
  {
    eventType: 'orders/fulfilled',
    title: 'Shopify - Order Fulfilled',
    sourceEventLabel: 'Order fulfillment',
    description: 'Fires when an order is shipped.',
    category: 'order',
    payloadSchema: buildPayloadSchema(ORDER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('orders/fulfilled', {
      orderNumber: '#1042',
      fulfillmentStatus: 'fulfilled',
    }),
    useCases: [
      {
        title: 'Shipment notification',
        description: 'Notify the shopper that their order is on its way.',
      },
    ],
  },
  {
    eventType: 'checkouts/create',
    title: 'Shopify - Checkout Created',
    sourceEventLabel: 'Checkout creation',
    description:
      'Fires when a checkout is created. Note: as of March 2026 the checkout identifier is `token`, not `id`.',
    category: 'checkout',
    payloadSchema: buildPayloadSchema(CHECKOUT_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('checkouts/create', {
      totalPrice: '49.95',
      currency: 'USD',
      abandonedCheckoutUrl: 'https://acme.myshopify.com/checkouts/c/abandoned',
    }),
    useCases: [
      {
        title: 'Abandoned-cart Stage 1',
        description: 'Send a 1hr nudge for a checkout that did not convert.',
      },
    ],
  },
  {
    eventType: 'checkouts/update',
    title: 'Shopify - Checkout Updated',
    sourceEventLabel: 'Checkout update',
    description: 'Fires when a checkout is updated.',
    category: 'checkout',
    payloadSchema: buildPayloadSchema(CHECKOUT_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('checkouts/update', {
      totalPrice: '54.95',
      currency: 'USD',
    }),
    useCases: [
      {
        title: 'Abandoned-cart Stage 2',
        description:
          'Send a 24hr reminder when the checkout still has not converted.',
      },
    ],
  },
  {
    eventType: 'fulfillments/create',
    title: 'Shopify - Fulfillment Created',
    sourceEventLabel: 'Fulfillment creation',
    description: 'Fires when a fulfillment is created.',
    category: 'fulfillment',
    payloadSchema: buildPayloadSchema(FULFILLMENT_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('fulfillments/create', {
      trackingNumber: '1Z999AA10123456789',
      trackingUrl: 'https://www.ups.com/track?tracknum=1Z999AA10123456789',
      fulfillmentStatus: 'success',
    }),
    useCases: [
      {
        title: 'Shipment notification',
        description:
          'Notify the shopper that their order is on its way with tracking.',
      },
    ],
  },
  {
    eventType: 'fulfillments/update',
    title: 'Shopify - Fulfillment Updated',
    sourceEventLabel: 'Fulfillment update',
    description: 'Fires when a fulfillment is updated (status change).',
    category: 'fulfillment',
    payloadSchema: buildPayloadSchema(FULFILLMENT_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('fulfillments/update', {
      trackingNumber: '1Z999AA10123456789',
      trackingUrl: 'https://www.ups.com/track?tracknum=1Z999AA10123456789',
      fulfillmentStatus: 'success',
    }),
    useCases: [
      {
        title: 'Delivery-completed thank-you',
        description: 'Trigger a thank-you / review request post-delivery.',
      },
    ],
  },
]

export function isSupportedShopifyEvent(
  eventType: string
): eventType is SupportedShopifyEventType {
  return SUPPORTED_SHOPIFY_EVENT_SET.has(eventType)
}
